import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import {
  useReactTable,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
} from '@tanstack/react-table'

import Table from './TableComponents/Table.jsx'

import PropTypes from 'prop-types'
import { defaultCompare } from '../XtractTableUtils.jsx'
import { useCompareDeepEffect } from 'hooks/useCompareDeepEffect.js'
import { usePatient } from 'hooks/useURL'
import TablePresetsButton from './TableState/TablePresetsButton.jsx'
import useTableState from './TableState/useTableState.js'
import useControlledTableProperty from './Utils/useControlledTableProperty.js'
import DownloadCSVButton from './CSV/DownloadCSVButton.jsx'
import PaginationControl from './Pagination/PaginationControl.jsx'
import usePagination from './Pagination/usePagination.js'
import ColumnChooserMenu from './ColumnVisibility/ColumnChooserMenu.jsx'
import useColumnSizing from './ColumnSizing/useColumnSizing.js'
import {ShowFiltersCheckbox} from './Filters/Filters.jsx'
import { isFilterValueEmpty } from './Filters/formatFilterValue.js'
import useFilters from './Filters/useFilters.js'
import RefreshButton from './TableComponents/RefreshButton.jsx'
import useColumnVisibility from './ColumnVisibility/useColumnVisibility.js'
import _ from 'lodash'

function XtractTable({
  data,
  columns: rawColumns,
  className = '',
  userStateKey,
  rightControlComponent = () => {},
  showControls = true,
  showPagination = true,
  pageSizeOptions = [10, 20, 50],
  downloadFileName = 'summit_download',
  getRowCanExpand = () => true,
  columnsCompatibleWithV7 = true,
  matchesCount,
  style: propsStyle = { height: '100%', width: '100%' },
  debounceMs: propsDebounceMs = 500,
  columnSizing: propsColumnSizing,
  onColumnSizingChange: propsOnColumnSizingChange,
  columnFilters: propsColumnFilters,
  onColumnFiltersChange: propsOnColumnFiltersChange = () => null,
  onPaginationChange: propsOnPaginationChange,
  onInitComplete: propsOnInitComplete = () => null,
  manual: propsManual,
  showFilters: propsShowFilters,
  renderSubComponent: propsRenderSubComponent = () => {},
  defaultColumnVisibility,
  onColumnVisibilityChange,
  columnChooserStyles = {},
  isModal = false,
  useFixedSizing = true,
  useDummyRows = true,
  // When true, column visibility always reverts to the column `show` defaults on
  // open (persisted per-user column selection is ignored; filters/sort/sizing are
  // still restored). Opt-in—off for every table that doesn't pass it.
  resetColumnVisibilityToDefaults = false,
  ...props
}) {

  const patientId = usePatient()

  const columns = useMemo(() => {
    return rawColumns.map(column => {
      let newColumn = { ...column }
      if (column.accessorKey === undefined && column.accessorFn === undefined) {
        newColumn = {
          ...column,
          accessorKey: column.id
        }
      }
      return newColumn
    })
  }, [rawColumns])

  const defaultSettings = useMemo(() => {
    return {
      showFilters: showFilters || false,
      sorted: sorting || [],
      page: pagination?.pageIndex || 0,
      pageSize: pagination?.pageSize || 20,
      columnSizing: columnSizing || {},
      filtered: columnFilters || [],
      selectedColumns: columnVisibility
    }
  }, [
    sorting,
    showFilters,
    columnSizing,
    columnFilters,
    columnVisibility,
    pagination?.pageSize,
    pagination?.pageIndex
  ])

    //////////////////column sizing/////////////////////////////
  const {columnSizing, setColumnSizing} = useColumnSizing({defaultColumnSizing: props.columnSizing, onColumnSizeChange: propsOnColumnSizingChange, table})

  ///////////////////////row selection/////////////////////////////
  const [rowSelection, setRowSelection] = useControlledTableProperty({
    initialValue: {},
    controlledValue: props.rowSelection,
    name: 'rowSelection',
    onChangeFn: props.onRowSelectionChange
  })

  //////////////////////expanded//////////////////////////
  const [expanded, setExpanded] = useControlledTableProperty({
    initialValue: {},
    controlledValue: props.expanded,
    name: 'expanded',
    onChangeFn: props.onExpandedChange
  })

  /////////////////filters////////////////////////////////
  const {
    columnFilters,
    setColumnFilters,
    showFilters,
    setShowFilters
  } = useFilters({
    defaultFilters: propsColumnFilters,
    onFilterChange: propsOnColumnFiltersChange,
    filtersVisible: propsShowFilters,
    onFiltersVisibleChange: props.onShowFiltersChange,
    table
  })

  // tracks keystrokes typed into filter inputs that haven't yet flushed through
  // their debounce. Counter-based so multiple inputs typed in quick succession
  // each contribute +1 / -1 and the overlay stays up until the last one settles.
  //
  // For manual tables there are TWO stacked debounces between keystroke and
  // setLoading(true): the DebouncedInput debounce (1000ms) and the internal
  // debouncedFetchData (propsDebounceMs). After the first settles we still
  // have to wait the second out before props.loading rises. Holding the
  // overlay through that second window prevents a visible gap.
  //
  // The loading-transition useEffect below provides the seamless handoff:
  // once props.loading rises we cancel the hold (overlay continues via loading).
  const pendingFilterCountRef = useRef(0)
  const pendingFilterClearTimerRef = useRef(null)
  const [pendingFilter, setPendingFilter] = useState(false)

  // refs so onImmediateFilterChange can stay stable across renders, and so
  // DebouncedInput's effect won't reset its debounce timer mid-typing
  const propsManualRef = useRef(propsManual)
  propsManualRef.current = propsManual
  const propsDebounceMsRef = useRef(propsDebounceMs)
  propsDebounceMsRef.current = propsDebounceMs

  const onImmediateFilterChange = useCallback(isPending => {
    if (isPending) {
      pendingFilterCountRef.current += 1
      if (pendingFilterClearTimerRef.current) {
        clearTimeout(pendingFilterClearTimerRef.current)
        pendingFilterClearTimerRef.current = null
      }
      setPendingFilter(true)
    } else {
      const next = pendingFilterCountRef.current - 1
      if (next < 0 && process.env.NODE_ENV !== 'production') {
        console.warn('XtractTable: pendingFilter counter underflow - this indicates a bug in the filter callback wiring')
      }
      pendingFilterCountRef.current = Math.max(0, next)
      if (pendingFilterCountRef.current === 0) {
        if (pendingFilterClearTimerRef.current) {
          clearTimeout(pendingFilterClearTimerRef.current)
        }
        // Hold long enough to bridge the internal debouncedFetchData on
        // manual tables, with a small buffer for the render+effect cycle.
        // For client-side tables the hold is brief, just long enough for
        // the table to render new rows before the overlay clears.
        const holdMs = propsManualRef.current
          ? (propsDebounceMsRef.current || 500) + 200
          : 100
        pendingFilterClearTimerRef.current = setTimeout(() => {
          pendingFilterClearTimerRef.current = null
          if (pendingFilterCountRef.current === 0) {
            setPendingFilter(false)
          }
        }, holdMs)
      }
    }
  }, [])

  // When props.loading rises during the hold window, cancel the pending clear
  // and let loading own the overlay. This is the seamless handoff that
  // eliminates the gap between debounce settle and parent setLoading(true).
  useEffect(() => {
    if (props.loading && pendingFilterClearTimerRef.current) {
      clearTimeout(pendingFilterClearTimerRef.current)
      pendingFilterClearTimerRef.current = null
      setPendingFilter(false)
    }
  }, [props.loading])

  useEffect(() => {
    return () => {
      if (pendingFilterClearTimerRef.current) {
        clearTimeout(pendingFilterClearTimerRef.current)
      }
    }
  }, [])

  const tableDivRef = useRef()
  useCompareDeepEffect(() => {
    // when filters change, scroll back to the start of the page
    tableDivRef.current.scrollTop = 0
    // when filters change, go back to the first page
    setPagination({
      ...pagination,
      pageIndex: 0
    })
    // pagination intentionally excluded; only update when filters change
  }, [columnFilters])

  ///////////////////storting///////////////////////////////
  const [sorting, setSorting] = useControlledTableProperty({
    initialValue: props.defaultSorting || [],
    controlledValue: props.sorting,
    name: 'sorting',
    onChangeFn: props.onSortingChange
  })

  //////////////////pagination/////////////////////////////
  const {
    setPagination,
    pagination
  } = usePagination({
    table,
    dataLength: data.length,
    showPagination,
    pageSizeOptions,
    pagination: props?.pagination,
    defaultPageSize: props?.defaultPageSize,
    userStateKey,
    onPaginationChange: propsOnPaginationChange
  })

  /////////////////////// visibility //////////////////////
  const {columnVisibility, setColumnVisibility} = useColumnVisibility({defaultColumnVisibility, onColumnVisibilityChange})

  //////////////////// table construction ////////////////////////////
  // manualPagination skips TanStack's client-side slice, so cap data to pageSize
  // to keep visible rows consistent with the selector while a fetch is in flight.
  const paginatedData =
    (propsManual || props.manualPagination) &&
    pagination?.pageSize &&
    data.length > pagination.pageSize
      ? data.slice(0, pagination.pageSize)
      : data

  const table = useReactTable({
    data: paginatedData,
    columns,
    defaultColumn: {
      filterFn: 'includesString',
      sortingFn: (rowA, rowB, columnId) => {
        return defaultCompare(rowA.getValue(columnId), rowB.getValue(columnId))
      },
      size: 500,
      ...props.defaultColumn
    },
    state: {
      columnVisibility,
      columnFilters,
      pagination,
      sorting,
      columnSizing,
      rowSelection,
      expanded
    },
    enableFilters: true,
    enableSorting: props.enableSorting,
    manualPagination: propsManual || props.manualPagination,
    manualFiltering: propsManual || props.manualFiltering,
    manualSorting: propsManual || props.manualSorting,
    enableColumnResizing: props.enableColumnResizing,
    rowCount: matchesCount,
    pageCount: propsManual && matchesCount && pagination?.pageSize
      ? Math.ceil(matchesCount / pagination?.pageSize)
      : pagination?.pageSize
        ? Math.ceil(data.length / pagination?.pageSize)
        : 0,
    columnResizeMode: 'onChange',
    getRowCanExpand: getRowCanExpand,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
    sortDescFirst: false
  })

  useEffect(() => {
    const nextRowSelection = props.rowSelection || {}
    const currentRowSelection = table.getState?.().rowSelection || {}
    if (!_.isEqual(currentRowSelection, nextRowSelection)) {
      table.setRowSelection(nextRowSelection)
    }
  }, [props.rowSelection, table])

  useEffect(() => {
    const nextExpanded = props.expanded || {}
    const currentExpanded = table.getState?.().expanded || {}
    if (!_.isEqual(currentExpanded, nextExpanded)) {
      table.setExpanded(nextExpanded)
    }
  }, [props.expanded, table])

  useEffect(() => {
    const nextSorting = props.sorting || (props.defaultSorting || [])
    const currentSorting = table.getState?.().sorting || []
    if (!_.isEqual(currentSorting, nextSorting)) {
      table.setSorting(nextSorting)
    }
    // props.defaultSorting not a dependency since it should only be set initially
  }, [props.sorting, table])

  const {
    marshalSettings,
    restoreState
  } = useTableState({
    table,
    columns,
    enableMultiSort: props.enableMultiSort,
    userStateKey,
    patientId,
    columnsCompatibleWithV7,
    defaultSettings,
    onInitComplete: propsOnInitComplete,
    pagination,
    pageSizeOptions,
    setShowFilters,
    setColumnFilters,
    setPagination,
    showFilters,
    resetColumnVisibilityToDefaults
  })

  /////////////////// table height //////////////////////////////
  const tableRef = useRef()
  const { height, ref } = useResizeDetector()
  const [tableHeight, setTableHeight] = useState(9999)
  useEffect(() => {
    const updateTableHeight = () => {
      if (tableRef?.current && height) {
        const parentHeight = height;
        setTableHeight(Math.max(32, parentHeight));
      } else {
        setTableHeight(9999);
      }
    };

    updateTableHeight();
  }, [height, tableRef?.current?.clientHeight, data, propsStyle.height]);

  // Scope these selectors to this table instance's own DOM subtree (via the
  // resize-detector root ref, which is attached to the table's outer div).
  // A document-global jQuery('.controls-area') / jQuery('.-pagination') returns
  // the FIRST match anywhere in the page, so when another window (e.g. Inventory)
  // is mounted at the same time, this table would measure the wrong (taller)
  // element and collapse its own scroll height to near zero.
  const controlsHeight = showControls
    ? jQuery(ref.current).find('.controls-area').outerHeight(true) || 0
    : 0
  const paginationHeight = showPagination
    ? jQuery(ref.current).find('.-pagination').outerHeight(true) || 0
    : 0
  const calculatedHeight = Math.min(
    tableHeight,
    height - (controlsHeight + paginationHeight)
  );

  ///////////////////// fetch debouncing //////////////////////////
  const noDebounceRef = useRef(true)
  const fetchTimerRef = useRef(null)
  const debouncedFetchData = useCallback((pagination, sorting, columnFilters) => {
    if (typeof props.onFetchData === 'function') {
      const debounceMs = noDebounceRef.current || !propsManual
        ? 0
        : propsDebounceMs

      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current)
        fetchTimerRef.current = null
      }

      fetchTimerRef.current = setTimeout(() => {
        noDebounceRef.current = false
        props.onFetchData(pagination, sorting, columnFilters)
      }, debounceMs)
    }
    // props intentionally excluded; its salient components are included instead
  }, [propsDebounceMs, props.onFetchData, propsManual])

  // call the debounced fetch function whenever the table state changes
  useCompareDeepEffect(() => {
    debouncedFetchData(pagination, sorting, columnFilters)
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    columnFilters,
    patientId
  ])

  const controlsArea = showControls => {
    if (showControls) {
      return (
        <div className={'controls-area'}>
          <div className={'left-controls-area'}>
            <ColumnChooserMenu
              visible={showControls.columns !== false}
              table={table}
              isModal={isModal}
              columnChooserStyles={columnChooserStyles}
            />
            <ShowFiltersCheckbox
              visible={showControls.filters !== false}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              setColumnFilters={setColumnFilters}
              activeFilterCount={(columnFilters || []).filter(f => {
                const col = table.getColumn?.(f.id)
                return !isFilterValueEmpty(f.value) &&
                  !(col?.columnDef?.isFilterCleared?.(f.value) ?? false)
              }).length}
            />
            <RefreshButton
              visible = {showControls.refresh !== false && propsManual}
              onClick={() => {
                props.onFetchData(pagination, sorting, columnFilters)
              }}
            />
            <TablePresetsButton
              visible={showControls.presets !== false}
              userStateKey={userStateKey}
              defaultSettings={defaultSettings}
              marshalSettings={marshalSettings}
              restoreState={restoreState}
            />
            <DownloadCSVButton
              visible={showControls.download !== false}
              pageSizeOptions={pageSizeOptions}
              table={table}
              downloadFileName={downloadFileName}
              propsManual={propsManual}
              matchesCount={matchesCount}
              pagination={pagination}
              dataLength={data.length}
            />
          </div>
          <div className={'right-controls-area'}>
            {showControls.rightControls !== false
              ? rightControlComponent(props)
              : null}
          </div>
        </div>
      )
    } else {
      return null
    }
  }

  return (
    <div ref={ref} style={propsStyle}>
      <div className={'xtract-table ' + className}>
        {controlsArea(showControls)}
        <div className="no-scroll">
          <div
            className="table-div"
            ref={tableDivRef}
            style={
              useFixedSizing
                ? {
                    height: `${calculatedHeight}px`,
                    minHeight: `${calculatedHeight}px`
                  }
                : undefined
            }
          >
            <Table
              ref={tableRef}
              table={table}
              showFilters={showFilters}
              manual={propsManual || props.manualFiltering}
              renderSubComponent={propsRenderSubComponent}
              showControls={showControls}
              useDummyRows={useDummyRows}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              columns={columns}
              pageSize={pagination.pageSize}
              {...props}
              onImmediateFilterChange={onImmediateFilterChange}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PaginationControl
            visible={showPagination}
            pageSizeOptions={pageSizeOptions}
            pagination={pagination}
            table={table}
            manual={propsManual || props.manualPagination}
            matchesCount={matchesCount}
          />
        </div>
        {props.loading || pendingFilter ? (
          <div
            style={{
              zIndex: '100',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.5)'
            }}
          ></div>
        ) : null}
      </div>
    </div>
  )
}

XtractTable.propTypes = {
  // the table data
  data: PropTypes.array.isRequired,

  // array of column configurations
  // these are taken from react-table (see https://tanstack.com/table/v8/docs/guide/overview) with a few additions:
  //
  //   show           - a boolean that indicates if the column should be shown be default
  //                    default is true
  //   showable       - a boolean that indicates if the column is showable at all
  //                    if not, it does not appear in the column chooser dropdown
  //                    default is true
  //   classes        - a string that is used to set the classes for DIVs containing headers and cells
  //                    this allows for a simple way of justifying text in a column, e.g.,
  //                      classes='right'
  //                    causes the header text and the cell contents to be right-aligned
  //   isPII          - a boolean to flag columns that are PII; used when downloading CSVs to alert the user
  //                    default is false
  //   downloadText   - a function that is used to compute the text for downloading
  //                    this comes into play when the value displayed on the screen is HTML, but needs to
  //                    be plain text when downloading
  //                    if this is set to false, then the column is not included in downloads (particularly
  //                    used when the contents of the table cell is a button)
  //   isFilterCleared - optional (value) => boolean. Return true when the current filter value represents
  //                    an effectively-unfiltered state (e.g. a multi-select dropdown with every option
  //                    selected). When true, the column does not get the "filter active" header indicator
  //                    and is excluded from the badge count on the show-filters toggle.
  //                    Empty values (null, '', empty array, or an object with no non-empty values --
  //                    e.g. the {} a cleared date-range filter produces) are already treated as
  //                    cleared by default, so this is only needed for non-empty "unfiltered" states.
  //                    Example for a MultiSelectDropdownFilter with includeSelectAll:
  //                      isFilterCleared: value => Array.isArray(value) && value.length === options.length
  //   formatFilterValue - optional (value, column) => string. Customize how the column header tooltip
  //                    renders the active filter. Needed when the filter value is a primitive whose
  //                    display label lives only inside the Filter component (e.g. a DropdownMenu filter
  //                    that stores 'y' but displays 'Yes'). The default formatter handles strings,
  //                    arrays of strings, arrays of option objects (via label/name/displayname or
  //                    option[column.id]), and {min, max, ...} range objects.
  //                    Example for a DropdownMenu filter:
  //                      formatFilterValue: value =>
  //                        ({y: 'Yes', n: 'No', either: 'Show all'})[value] ?? String(value)

  columns: PropTypes.array.isRequired,

  // when data is paged from the server, use this to specify how many total rows matched search criteria
  matchesCount: PropTypes.number,

  // should we show the Column Chooser and the Filter Checkbox?
  // true/false for entire controls area or {filters: bool, columns: bool, rightControls: bool} for individual controls
  showControls: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),

  // should we show the tiny chooser for selecting columns (the vertical ellipsis in the first column header?)
  showTinyColumnChooser: PropTypes.bool,

  // should we show the pagination control?
  showPagination: PropTypes.bool,

  // function or component for right-hand side of controls area
  rightControlComponent: PropTypes.func,

  // if present, name used for downloaded CSV
  downloadFileName: PropTypes.string,

  // key for persisting table configuration in localStorage
  // if present, state is stored upon close and restored upon open
  userStateKey: PropTypes.string,

  // number of milliseconds to debounce calls to fetchData
  debounceMs: PropTypes.number,

  // type of row click that is supported: 'none', 'single-select', 'single-select-sticky', 'multi-select', 'single-expand', or 'multi-expand'
  rowOnClickAction: PropTypes.string,

  // are the column definitions compatible with version 7 definitions for the same table?
  // (this has a bearing on being able to read stored table configurations from localStorage)
  // (because the columns were referenced by numerical index rather than index name)
  columnsCompatibleWithV7: PropTypes.bool,

  // can the given row expand? default is "all rows can expand"
  getRowCanExpand: PropTypes.func,

  // optional function to render subrows
  renderSubComponent: PropTypes.func,

  // callback to parent to get properties for a header rows, parameters: (props)
  getTheadTrProps: PropTypes.func,

  // callback to parent to get properties for a header row cells, parameters: (props, column)
  getTheadThProps: PropTypes.func,

  // callback to parent to get properties for a row, parameters: (row, props, isSubComponent)
  getTrProps: PropTypes.func,

  // callback to parent to get properties for a cell, parameters: (row, props, column, isSubComponent)
  getTdProps: PropTypes.func,

  // style that is added to the <table> component
  style: PropTypes.object,

  // what message to display when there is no data to show
  noDataText: PropTypes.string,

  /// ////////////////////////////////////////////////
  //
  // Controllable properties

  // initial value for showFilters checkbox
  showFilters: PropTypes.bool,

  // True if the table is displayed int he modal. This adjusts zIndex of things like column chooser
  // menu to ensure they appear over the modal
  isModal: PropTypes.bool,

  // column visibility
  // object where each element's key is an index into the data array and the value is a boolean
  columnVisibility: PropTypes.object,

  // value for columnFilters
  // array of objects, each object is {column_id, value}
  columnFilters: PropTypes.array,

  // column width sizing
  // object where each element's key is an index into the data array and the width
  columnSizing: PropTypes.object,

  // sorting
  // array of objects, each object is {column_id, desc}
  sorting: PropTypes.array,

  // row selection
  // object where each element's key is an index into the data array and the value is a boolean
  rowSelection: PropTypes.object,

  // row expansion
  // object where each element's key is an index into the data array and the value is a boolean
  expanded: PropTypes.object,

  // pagination
  // object {pageIndex (0-based), pageSize}
  pagination: PropTypes.object,

  // callback function to call when "Show filters?" checkbox changes
  onShowFiltersChange: PropTypes.func,

  // callback function to call when column is shown/hidden
  onColumnVisibilityChange: PropTypes.func,

  // callback function to call when column filters change
  onColumnFiltersChange: PropTypes.func,

  // callback function to call when column size is changed
  onColumnSizingChange: PropTypes.func,

  // callback function to call when column sorting changes
  onSortingChange: PropTypes.func,

  // callback function to call when row selection changes
  onRowSelectionChange: PropTypes.func,

  // callback function to call when rows are expanded or collapsed
  onExpandedChange: PropTypes.func,

  // callback function to call when pagination changes
  onPaginationChange: PropTypes.func,

  // callback function called after table is initially rendered and restored from localStore
  onInitComplete: PropTypes.func
}

export default XtractTable

